import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  useFieldOversightAgronomists,
  useFieldOversightLatestCheckIns,
  useFieldOversightMissedReport,
  useFieldOversightReportConfiguration,
  useUpdateFieldOversightReportConfiguration,
} from "@/features/production/hooks/useFieldOversight";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { toast } from "sonner";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const formatActivityStatus = (value?: string | null) => {
  const labels: Record<string, string> = {
    FarmerVisit: "Farmer visit",
    InTransit: "In transit",
    FieldWork: "Field work",
    OfficeAdmin: "Office / admin",
    Other: "Other",
  };
  return value ? labels[value] ?? value : "Not recorded";
};

const normalizeTab = (value: string | null) => {
  if (value === "missed" || value === "missed-check-ins") return "missed";
  if (value === "schedule") return "schedule";
  return "latest";
};

const normalizeStatusFilter = (value: string | null) => {
  if (value === "overdue" || value === "on-track") return value;
  return "all";
};

const normalizeDate = (value: string | null) => {
  if (!value) return "";
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
};

export default function FieldOversightPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => normalizeTab(searchParams.get("tab")));
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [statusFilter, setStatusFilter] = useState(() => normalizeStatusFilter(searchParams.get("status")));
  const [fromDate, setFromDate] = useState(() => normalizeDate(searchParams.get("fromDate")));
  const [toDate, setToDate] = useState(() => normalizeDate(searchParams.get("toDate")));
  const [agronomistUserId, setAgronomistUserId] = useState(() => searchParams.get("agronomist") ?? "");
  const [agronomistSearch, setAgronomistSearch] = useState("");
  const [recipientEmails, setRecipientEmails] = useState("");
  const [reportFrequency, setReportFrequency] = useState<"daily" | "weekly" | "off">("daily");
  const [deliveryTime, setDeliveryTime] = useState("17:00");
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canConfigure = hasPermission("field_oversight.configure");
  const debouncedSearch = useDebounce(search, 350);
  const paramsString = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(paramsString);
    const nextTab = normalizeTab(params.get("tab"));
    const nextSearch = params.get("q") ?? "";
    const nextStatus = normalizeStatusFilter(params.get("status"));
    const nextFromDate = normalizeDate(params.get("fromDate"));
    const nextToDate = normalizeDate(params.get("toDate"));
    const nextAgronomistUserId = params.get("agronomist") ?? "";

    setActiveTab((current) => (current === nextTab ? current : nextTab));
    setSearch((current) => (current === nextSearch ? current : nextSearch));
    setStatusFilter((current) => (current === nextStatus ? current : nextStatus));
    setFromDate((current) => (current === nextFromDate ? current : nextFromDate));
    setToDate((current) => (current === nextToDate ? current : nextToDate));
    setAgronomistUserId((current) => current === nextAgronomistUserId ? current : nextAgronomistUserId);
  }, [paramsString]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab !== "latest") next.set("tab", activeTab);
    if (search) next.set("q", search);
    if (statusFilter !== "all") next.set("status", statusFilter);
    if (fromDate) next.set("fromDate", fromDate);
    if (toDate) next.set("toDate", toDate);
    if (agronomistUserId) next.set("agronomist", agronomistUserId);

    const nextString = next.toString();
    if (nextString !== paramsString) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    search,
    statusFilter,
    fromDate,
    toDate,
    agronomistUserId,
    paramsString,
    setSearchParams,
  ]);

  const latestQuery = useFieldOversightLatestCheckIns(
    debouncedSearch,
    { enabled: activeTab === "latest" }
  );
  const reportQuery = useFieldOversightMissedReport({
    fromUtc: fromDate ? new Date(`${fromDate}T00:00:00.000Z`).toISOString() : undefined,
    toUtc: toDate ? new Date(`${toDate}T23:59:59.999Z`).toISOString() : undefined,
    agronomistUserId: agronomistUserId || undefined,
  }, { enabled: activeTab === "missed" });
  const agronomistsQuery = useFieldOversightAgronomists(activeTab === "missed");
  const configurationQuery = useFieldOversightReportConfiguration(activeTab === "schedule");
  const updateConfiguration = useUpdateFieldOversightReportConfiguration();

  useEffect(() => {
    if (!configurationQuery.data) return;
    setRecipientEmails(configurationQuery.data.recipientEmails);
    setReportFrequency(configurationQuery.data.frequency);
    setDeliveryTime(configurationQuery.data.deliveryTime);
  }, [configurationQuery.data]);

  const latestRows = useMemo(() => latestQuery.data ?? [], [latestQuery.data]);
  const filteredLatestRows = useMemo(() => {
    return latestRows.filter((row) => {
      if (statusFilter === "overdue") return row.isOverdue;
      if (statusFilter === "on-track") return !row.isOverdue;
      return true;
    });
  }, [latestRows, statusFilter]);

  const overdueCount = useMemo(
    () => latestRows.filter((row) => row.isOverdue).length,
    [latestRows]
  );
  const report = reportQuery.data;
  const agronomists = agronomistsQuery.data ?? [];
  const visibleAgronomists = agronomists.filter((item) =>
    item.agronomistName.toLowerCase().includes(agronomistSearch.trim().toLowerCase())
  );
  const selectedAgronomist = agronomists.find((item) => item.userId === agronomistUserId);

  const saveReportConfiguration = () => {
    updateConfiguration.mutate(
      { recipientEmails, frequency: reportFrequency, deliveryTime },
      {
        onSuccess: () => toast.success("Email report schedule saved"),
        onError: () => toast.error("Could not save the email report schedule"),
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Field Oversight</h1>
          <p className="mt-1 text-muted-foreground">
            Monitor agronomist check-ins and missed prompt trends.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="latest">Latest Check-Ins</TabsTrigger>
          <TabsTrigger value="missed">Missed Check-Ins</TabsTrigger>
          <TabsTrigger value="schedule">Email Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="latest" className="space-y-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Search agronomist name, username, phone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-md"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="overdue">Overdue only</SelectItem>
                <SelectItem value="on-track">On track only</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
              }}
            >
              Clear
            </Button>
            <Button variant="outline" onClick={() => void latestQuery.refetch()}>
              Refresh
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-background p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Agronomists</p>
              <p className="mt-1 text-2xl font-semibold">{latestRows.length}</p>
            </div>
            <div className="rounded-lg border bg-background p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Overdue</p>
              <p className="mt-1 text-2xl font-semibold text-red-600">{overdueCount}</p>
            </div>
            <div className="rounded-lg border bg-background p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">On Track</p>
              <p className="mt-1 text-2xl font-semibold text-emerald-700">{Math.max(latestRows.length - overdueCount, 0)}</p>
            </div>
          </div>

          {latestQuery.isLoading ? (
            <div className="rounded-lg border bg-muted/40 p-6 text-sm text-muted-foreground">
              Loading latest check-ins...
            </div>
          ) : latestQuery.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
              Failed to load latest check-ins.
            </div>
          ) : filteredLatestRows.length === 0 ? (
            <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
              No agronomist check-in records found.
            </div>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agronomist</TableHead>
                    <TableHead>Last Check-In</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Map</TableHead>
                    <TableHead>Prompt State</TableHead>
                    <TableHead>Interval</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLatestRows.map((row) => (
                    <TableRow key={row.userId}>
                      <TableCell>
                        <div className="space-y-0.5">
                          <p>{row.agronomistName}</p>
                          <p className="text-xs text-muted-foreground">{row.phoneNumber || "-"}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <p>{formatDateTime(row.lastCheckedInAt)}</p>
                          <p className="text-xs text-muted-foreground">
                            {row.minutesSinceLastCheckIn == null ? "-" : `${row.minutesSinceLastCheckIn} min ago`}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.lastLocationName ? (
                          <div className="space-y-0.5">
                            <p>{row.lastLocationName}</p>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Badge variant="outline">{formatActivityStatus(row.lastActivityStatus)}</Badge>
                              {row.lastFarmerOrFarmName ? (
                                <span className="text-xs text-muted-foreground">{row.lastFarmerOrFarmName}</span>
                              ) : null}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {row.lastLatitude?.toFixed(5)}, {row.lastLongitude?.toFixed(5)}
                            </p>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {row.lastLatitude != null && row.lastLongitude != null ? (
                          <a
                            href={`https://www.google.com/maps?q=${row.lastLatitude},${row.lastLongitude}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-sky-700 hover:underline"
                          >
                            Open Map
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-xs text-muted-foreground">
                          <p>Prompted: {formatDateTime(row.lastPromptedAt)}</p>
                          <p>Responded: {formatDateTime(row.lastRespondedAt)}</p>
                          <p>Missed: {row.lastPromptMissed ? "Yes" : "No"}</p>
                        </div>
                      </TableCell>
                      <TableCell>{row.pollingIntervalMinutes} min</TableCell>
                      <TableCell>
                        <Badge variant={row.isOverdue ? "destructive" : "default"}>
                          {row.isOverdue ? "Overdue" : "On Track"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="missed" className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Combobox
              value={agronomistUserId || null}
              onValueChange={(value: string | null) => {
                setAgronomistUserId(value ?? "");
                const selected = agronomists.find((item) => item.userId === value);
                setAgronomistSearch(selected?.agronomistName ?? "");
              }}
              inputValue={agronomistSearch}
            >
              <ComboboxInput
                placeholder="Search agronomists (all)"
                showClear={!!agronomistUserId}
                className="w-full"
                onChange={(event) => setAgronomistSearch(event.target.value)}
              />
              <ComboboxContent>
                <ComboboxList>
                  {visibleAgronomists.length === 0 && <ComboboxEmpty>No agronomists found.</ComboboxEmpty>}
                  {visibleAgronomists.map((item) => (
                    <ComboboxItem key={item.userId} value={item.userId}>
                      {item.agronomistName}
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              placeholder="From date"
            />
            <DatePicker
              value={toDate}
              onChange={setToDate}
              placeholder="To date"
              min={fromDate || undefined}
            />
            <div className="md:col-span-3 flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {selectedAgronomist ? `Showing missed check-ins for ${selectedAgronomist.agronomistName}` : "Showing all agronomists"}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => void reportQuery.refetch()}>
                  Refresh Report
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setAgronomistUserId("");
                    setAgronomistSearch("");
                  }}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          </div>

          {reportQuery.isLoading ? (
            <div className="rounded-lg border bg-muted/40 p-6 text-sm text-muted-foreground">
              Loading missed check-in report...
            </div>
          ) : reportQuery.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
              Failed to load missed check-in report.
            </div>
          ) : report ? (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Window</p>
                  <p className="mt-1 text-sm font-medium">{formatDateTime(report.fromUtc)} to {formatDateTime(report.toUtc)}</p>
                </div>
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Check-ins Missed</p>
                  <p className="mt-1 text-2xl font-semibold">{report.totalMissedPrompts}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Prompts that received no check-in</p>
                </div>
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Agronomists Affected</p>
                  <p className="mt-1 text-2xl font-semibold">{report.distinctAgronomists}</p>
                </div>
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Missed Per Affected Agronomist</p>
                  <p className="mt-1 text-2xl font-semibold">
                    {report.distinctAgronomists === 0
                      ? "0"
                      : (report.totalMissedPrompts / report.distinctAgronomists).toFixed(1)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agronomist</TableHead>
                      <TableHead>Check-ins Missed</TableHead>
                      <TableHead>Most Recent Missed Check-In</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          No missed prompts in this window.
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.items.map((item) => (
                        <TableRow key={item.userId}>
                          <TableCell>{item.agronomistName}</TableCell>
                          <TableCell>
                            <div className="font-medium">{item.missedCount} missed check-in{item.missedCount === 1 ? "" : "s"}</div>
                            <div className="text-xs text-muted-foreground">No response was recorded for these prompts</div>
                          </TableCell>
                          <TableCell>{formatDateTime(item.lastMissedAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <div className="rounded-lg border bg-background">
            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">Missed check-in email reports</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Send each recipient a separate report for every agronomist who missed a check-in during the reporting period.
              </p>
            </div>
            <div className="grid gap-5 p-5 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="report-emails">Email recipients</Label>
                <Textarea
                  id="report-emails"
                  value={recipientEmails}
                  onChange={(event) => setRecipientEmails(event.target.value)}
                  placeholder="manager@company.com, supervisor@company.com"
                  disabled={!canConfigure || configurationQuery.isLoading}
                />
                <p className="text-xs text-muted-foreground">Enter one or more email addresses, separated by commas.</p>
              </div>
              <div className="space-y-2">
                <Label>Send report</Label>
                <Select
                  value={reportFrequency}
                  onValueChange={(value) => setReportFrequency(value as "daily" | "weekly" | "off")}
                  disabled={!canConfigure || configurationQuery.isLoading}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Every day</SelectItem>
                    <SelectItem value="weekly">Every Monday (previous 7 days)</SelectItem>
                    <SelectItem value="off">Do not send automatically</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery-time">Delivery time</Label>
                <TimePicker
                  id="delivery-time"
                  value={deliveryTime}
                  onChange={setDeliveryTime}
                  minuteStep={15}
                  disabled={!canConfigure || configurationQuery.isLoading || reportFrequency === "off"}
                />
                <p className="text-xs text-muted-foreground">Uses the server's local time.</p>
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-3 border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  {canConfigure ? "Changes apply to the next scheduled report." : "You can view this schedule, but you do not have permission to change it."}
                </p>
                {canConfigure && (
                  <Button onClick={saveReportConfiguration} disabled={updateConfiguration.isPending || configurationQuery.isLoading}>
                    {updateConfiguration.isPending ? "Saving..." : "Save schedule"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
